<template>
  <div ref="item" class="file-view">
    <img
      v-if="isImage"
      class="file-view__image"
      :src="fileData"
    >
    <object
      v-else-if="isPDF || isPresentation || isDOC || isCALC"
      :class="objectClasses"
      :type="fileType"
      :data="fileData"
      @error="error = true"
    >
      <!-- Fallback - Will load if browser can't read the file as embed -->
      <div class="file-view__fallback">
        <div>{{ this.$t('message.NO_PREVIEW') }}</div>
        <a :href="fileLink" target="_blank">
          <SBtn
            class="file-view__download-btn"
            color="#00388C"
            dark
          >
            <Icon image left>{{ iconDownloadOutline }}</Icon>
            {{ this.$t('message.DOWNLOAD') }}
          </SBtn>
        </a>
      </div>
    </object>
  </div>
</template>

<script>
/**
 * File preview 'window' for FileViewer component
 *
 * @vue-prop {Object} [file] - File informations - Will be in loading state if no file is given.
 */

import { FETCH_FILE_DATA } from '@/store/modules/file/action-types';
// Import components
import Icon from '@/components/icon.vue';
import SBtn from '@/components/s-btn.vue';
// Import utils
import {
  RE_IMAGE_TYPE,
  FILE_TYPE_DOC,
  FILE_TYPE_DOCX,
  FILE_TYPE_ODT,
  FILE_TYPE_XLS,
  FILE_TYPE_XLSX,
  FILE_TYPE_ODS,
  FILE_TYPE_PPT,
  FILE_TYPE_PPTX,
  FILE_TYPE_ODP,
} from '@/utils/file';

const iconDownloadOutline = require('@/assets/img/download-medium-white.png');

export default {
  name: 'FileView',

  components: {
    Icon,
    SBtn,
  },

  props: {
    file: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      iconDownloadOutline,
      error: false,
    };
  },

  computed: {
    /** Object: Details of the displayed file */
    fileDetails() {
      return this.$store.state.file.files[`${this.file.id}`];
    },
    /**
     * Blob: File data as a blob
     * Url: The url of the file to get
     * */
    fileData() {
      if (this.isDOC || this.isCALC || this.isPresentation) {
        return `/server/file/${this.file.id}`;
      }
      return this.$store.state.file.fileData[`${this.file.id}`];
    },
    fileLink() {
      return `/server/file/download/${this.file.id}`;
    },
    fileType() {
      if (this.isDOC || this.isCALC) {
        return '';
      }
      return this.file.type;
    },
    isImage() {
      return RE_IMAGE_TYPE.test(this.file.type);
    },
    isPDF() {
      return this.file.type === 'application/pdf';
    },
    isDOC() {
      return this.file.type === FILE_TYPE_DOC || this.file.type === FILE_TYPE_DOCX || this.file.type === FILE_TYPE_ODT;
    },
    isCALC() {
      return this.file.type === FILE_TYPE_XLS || this.file.type === FILE_TYPE_XLSX || this.file.type === FILE_TYPE_ODS;
    },
    isPresentation() {
      return this.file.type === FILE_TYPE_PPT || this.file.type === FILE_TYPE_PPTX || this.file.type === FILE_TYPE_ODP;
    },
    objectClasses() {
      return {
        'file-view__object': true,
        'file-view__object--pdf': !this.error && this.isPDF && this.isDOC && this.isCALC,
      };
    },
  },

  created() {
    this.$store.dispatch(`file/${FETCH_FILE_DATA}`, { fileId: this.file.id });
  },
};
</script>

<style lang="scss">
.file-view {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;

  &__image {
    height: 100%;
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    width: 100%;
  }
  &__object {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;

    &--pdf {
      // For Safari: PDF background are transparent sometimes
      background-color: white;
    }
  }
  &__fallback {
    background-color: #fff;
    border-radius: 6px;
    padding: 20px;
    text-align: center;
    width: 300px;
  }
  &__download-btn {
    margin-top: 20px;
  }
}
</style>
